import React from "react";
import axios from "axios";
import CircularProgress from "@mui/material/CircularProgress";

import { FormFieldInput } from "../main/content/create_training_content/FormFieldInput";
import {
  visibleFields,
  buildFieldsById,
  resolvePiping,
} from "../main/content/create_training_content/formLogic";
import { isDisplayOnlyType } from "../main/content/create_training_content/formFieldHelpers";
import FormInfoScreen from "../main/content/create_training_content/FormInfoScreen";
import FormFieldImage from "../main/content/create_training_content/FormFieldImage";
import FormPlayer from "../main/content/take_training_employee/form_player/FormPlayer";
import {
  API_get_public_form,
  API_post_public_form_submit,
  API_post_public_form_opened,
} from "../constants";

/**
 * Public, unauthenticated form page (/f/<token>).
 *
 * Anyone with the link can fill and submit a form — no login, no app shell.
 * Data is fetched with bare axios (no auth token) from the AllowAny endpoint;
 * answer keys / option points are already stripped server-side. Submissions are
 * stored anonymously (never as a LearningFormCompletion).
 *
 * Two presentations: `slides` hands the form to the one-question-per-screen
 * FormPlayer (public mode); `list` (default) renders the scrolling page below.
 * Both apply display conditions + answer piping.
 */
function PublicFormView({ token }) {
  const [status, setStatus] = React.useState("loading"); // loading|ready|not_found|error|submitting|done
  const [form, setForm] = React.useState(null);
  const [responses, setResponses] = React.useState({});
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [submitError, setSubmitError] = React.useState(null);
  const [result, setResult] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_get_public_form}${token}`);
        if (cancelled) return;
        const payload = res?.data?.form;
        if (!payload) {
          setStatus("not_found");
          return;
        }
        setForm(payload);
        setStatus("ready");
      } catch (err) {
        if (cancelled) return;
        setStatus(err?.response?.status === 404 ? "not_found" : "error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Best-effort anonymous reach: bump the open counter once per browser.
  React.useEffect(() => {
    if (status !== "ready" || !token) return;
    const key = `public_form_opened_${token}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
    } catch (e) {
      // localStorage unavailable (private mode) — still fire once per load.
    }
    axios.post(API_post_public_form_opened, { share_token: token }).catch(() => {});
  }, [status, token]);

  const setResponse = (fieldId, value) =>
    setResponses((prev) => ({ ...prev, [fieldId]: value }));

  // Only fields whose display condition currently holds are shown, required,
  // or submitted (the server applies the same rules).
  const shownFields = visibleFields(form?.fields || [], responses);
  const fieldsById = buildFieldsById(form?.fields || []);
  const pipe = (text) => resolvePiping(text, responses, fieldsById);

  const requiredUnfilled = shownFields.some((f) => {
    if (isDisplayOnlyType(f.type)) return false;
    if (!f.required) return false;
    const v = responses[f.id];
    if (f.type === "checkbox") return !v;
    if (f.type === "yes_no") return !(v === true || v === false);
    if (Array.isArray(v)) return v.length === 0;
    return v === undefined || v === null || v === "";
  });
  const respondentMissing =
    !!form?.collect_respondent_info && (!name.trim() || !email.trim());

  const handleSubmit = async () => {
    if (requiredUnfilled || respondentMissing || status === "submitting") return;
    setStatus("submitting");
    setSubmitError(null);
    try {
      const shownIds = new Set(shownFields.map((f) => f.id));
      const payload = {};
      Object.keys(responses).forEach((k) => {
        if (shownIds.has(k)) payload[k] = responses[k];
      });
      const res = await axios.post(API_post_public_form_submit, {
        share_token: token,
        responses: payload,
        respondent_name: name.trim() || undefined,
        respondent_email: email.trim() || undefined,
      });
      setResult(res?.data || { ok: true });
      setStatus("done");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.system?.message ||
        "Couldn't submit. Please try again.";
      setSubmitError(msg);
      setStatus("ready");
    }
  };

  const resetForAnother = () => {
    setResponses({});
    setName("");
    setEmail("");
    setResult(null);
    setSubmitError(null);
    setStatus("ready");
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <CircularProgress sx={{ color: "#6d28d9" }} />
      </div>
    );
  }

  if (status === "not_found" || status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="text-center max-w-sm">
          <h1 className="text-xl font-semibold text-slate-900 mb-2">
            {status === "not_found"
              ? "This link isn't available"
              : "Something went wrong"}
          </h1>
          <p className="text-sm text-slate-500">
            {status === "not_found"
              ? "This form link may have been turned off or no longer exists."
              : "Please try again in a moment."}
          </p>
        </div>
      </div>
    );
  }

  // Survey-style presentation: the player owns the whole page (welcome, one
  // question per screen, thank-you, "submit another").
  if (form?.presentation === "slides") {
    return <FormPlayer mode="public" form={form} shareToken={token} />;
  }

  if (status === "done") {
    const scored =
      result?.scoring_enabled && result?.score !== null && result?.score !== undefined;
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="text-center max-w-sm">
          <div className="text-5xl mb-4">✅</div>
          <h1 className="text-2xl font-semibold text-slate-900 mb-2">
            {form?.thank_you_screen?.title || "Thanks! Your response was submitted."}
          </h1>
          {form?.thank_you_screen?.description && (
            <p className="text-sm text-slate-600 mb-4 whitespace-pre-wrap">
              {form.thank_you_screen.description}
            </p>
          )}
          {scored && (
            <p className="text-sm text-slate-600 mb-4">
              Your score: <span className="font-semibold">{result.score}</span>
              {result.is_passed === true
                ? " · Passed"
                : result.is_passed === false
                ? " · Not passed"
                : ""}
            </p>
          )}
          <button
            type="button"
            onClick={resetForAnother}
            className="mt-2 text-sm font-medium text-violet-700 hover:text-violet-800"
          >
            Submit another response
          </button>
        </div>
      </div>
    );
  }

  // ready | submitting
  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="mx-auto w-full max-w-xl bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-5">
        <h1 className="text-2xl font-bold text-slate-900">{form.name}</h1>
        {form.description && (
          <p className="mt-1 text-sm text-slate-600">{form.description}</p>
        )}
        {!form.collect_respondent_info && (
          <p className="mt-2 text-xs text-slate-500">
            Your name isn't collected with your answers.
          </p>
        )}

        <div className="mt-6 flex flex-col gap-5">
          {form.collect_respondent_info && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Your name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none transition-colors focus:border-violet-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Your email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none transition-colors focus:border-violet-700"
                />
              </div>
            </>
          )}

          {shownFields.map((f) => {
            if (f.type === "info_screen") {
              return (
                <FormInfoScreen
                  key={f.id}
                  label={pipe(f.label)}
                  help={pipe(f.help)}
                  imageUrl={f.image_url}
                  className="pt-2"
                />
              );
            }
            if (f.type === "heading") {
              return (
                <div key={f.id} className="pt-2">
                  <div className="text-sm font-semibold text-slate-900">
                    {pipe(f.label)}
                  </div>
                  {f.help && (
                    <p className="mt-1 text-xs text-slate-500 whitespace-pre-wrap">{pipe(f.help)}</p>
                  )}
                  <FormFieldImage url={f.image_url} size="inline" className="mt-2" />
                </div>
              );
            }
            return (
              <div key={f.id}>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {pipe(f.label)}
                  {f.required && <span className="text-red-500"> *</span>}
                </label>
                {f.help && f.type !== "checkbox" && (
                  <p className="text-xs text-slate-500 mb-2 whitespace-pre-wrap">
                    {pipe(f.help)}
                  </p>
                )}
                <FormFieldImage url={f.image_url} size="inline" className="mb-2" />
                <FormFieldInput
                  field={f.help ? { ...f, help: pipe(f.help) } : f}
                  value={responses[f.id]}
                  onChange={(val) => setResponse(f.id, val)}
                />
              </div>
            );
          })}
        </div>

        {submitError && (
          <div className="mt-4 text-sm text-red-600">{submitError}</div>
        )}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={requiredUnfilled || respondentMissing || status === "submitting"}
          className="mt-6 w-full rounded-xl py-3 text-base font-semibold text-white bg-violet-700 hover:bg-violet-800 transition-colors disabled:opacity-50"
        >
          {status === "submitting" ? "Submitting…" : "Submit"}
        </button>
      </div>
    </div>
  );
}

export default PublicFormView;
